import React, { useState, useEffect } from 'react'
import ChatPanel from 'components/chat'
import ProductTour, { TourStep } from 'components/tour'
import { useApplicationStore } from 'hooks/application'
import { HUB_BASE, HOME, INTEGRATIONS } from 'routes'
import { TOUR_STEP_STORAGE_KEY, TOUR_STORAGE_KEY } from 'app-constants'
import { useCustomViewPanel } from 'components/spreadsheet/components/custom-view/panel'

const MortaAgent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [tourSteps, setTourSteps] = useState<TourStep[]>([])
  const { supportChatContext, agentChatLaunchContext } = useApplicationStore()
  // On a Custom View's editor, the pencil and its docked panel are MortaAI already, so the floating one steps aside.
  const customViewPanel = useCustomViewPanel()
  const docked = customViewPanel.open || customViewPanel.editing

  // Restore tour from localStorage on mount
  useEffect(() => {
    const savedTour = localStorage.getItem(TOUR_STORAGE_KEY)
    if (savedTour) {
      let parsedTour: { steps?: TourStep[] } | null = null

      try {
        parsedTour = JSON.parse(savedTour)
      } catch (e) {
        localStorage.removeItem(TOUR_STORAGE_KEY)
      }

      const steps = parsedTour?.steps
      if (Array.isArray(steps) && steps.length > 0) {
        setTourSteps(steps)
      }
    }
  }, [])

  useEffect(() => {
    if (supportChatContext || agentChatLaunchContext) {
      setIsOpen(true)
    }
  }, [supportChatContext, agentChatLaunchContext])

  const handleStartTour = (steps: TourStep[]) => {
    // Detect current location and skip unnecessary navigation steps
    const currentPath = window.location.pathname
    let startStep = 0

    // Check if the tour starts with hub navigation steps
    const hasHubsLink = steps[0]?.target === "[data-tour='hubs-link']"
    const hasHubList = steps[1]?.target === "[data-tour='hub-list']"

    if (hasHubsLink && hasHubList) {
      // If already in a hub (URL like /hub/:id), skip both navigation steps
      if (currentPath.startsWith(HUB_BASE)) {
        startStep = 2
      }
      // If on home page, skip hubs-link step
      else if (currentPath === HOME) {
        startStep = 1
      }
    }

    // Check if the tour starts with integrations navigation steps
    const hasProfileButton = steps[0]?.target === "[data-tour='user-profile-button']"
    const hasIntegrationsLink = steps[1]?.target === "[data-tour='user-menu-integrations']"

    if (hasProfileButton && hasIntegrationsLink) {
      // If already on integrations page, skip both navigation steps
      if (currentPath === INTEGRATIONS) {
        startStep = 2
      }
    }

    // Save to localStorage BEFORE updating state, so the tour component can read it
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify({ steps }))
    localStorage.setItem(TOUR_STEP_STORAGE_KEY, startStep.toString())

    // Update state last - this triggers the tour component to initialize from localStorage
    setTourSteps(steps)
  }

  const handleCompleteTour = () => {
    setTourSteps([])
    localStorage.removeItem(TOUR_STORAGE_KEY)
    localStorage.removeItem(TOUR_STEP_STORAGE_KEY)
  }

  return (
    <div>
      {!docked && <ChatPanel isOpen={isOpen} onClose={() => setIsOpen(false)} onStartTour={handleStartTour} />}
      <ProductTour steps={tourSteps} run={tourSteps.length > 0} onComplete={handleCompleteTour} />

      {!docked && (
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
          className="group fixed bottom-6 right-6 w-[52px] h-[52px] rounded-full z-[1000] text-[22px] p-0 inline-flex items-center justify-center text-on-solid border-0 cursor-pointer transition-all bg-[linear-gradient(135deg,#1568e5,#2e7ff9,#6aa5fb)] bg-[length:200%_200%] animate-fab-gradient shadow-[0_4px_15px_rgba(46,127,249,0.4),0_1px_3px_rgba(0,0,0,0.1)] hover:scale-[1.08]! hover:shadow-[0_6px_24px_rgba(46,127,249,0.5),0_2px_6px_rgba(0,0,0,0.12)]! active:scale-[0.96]!"
          title="MortaAI"
          aria-label={isOpen ? 'Close MortaAI chat' : 'Open MortaAI chat'}
        >
          <span className="transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:rotate-90">
            {isOpen ? '✕' : '✦'}
          </span>
        </button>
      )}
    </div>
  )
}

export default MortaAgent
